import type { SxStyle } from '@aibees/shared/theme';
import { getColumnWidth } from '@aibees/shared/utils/table-column-sizes';
import {
  Box,
  TableCell,
  TableSortLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Icon } from '../icon';
import * as SX from './sx';
import type { TableColumn } from './types';
import { getSortIconName } from './utils';

type Props = {
  headCell: TableColumn<Record<string, any>>;
  order?: 'asc' | 'desc';
  orderBy?: string;
  colSpan?: number;
  isLast?: boolean;
  onSort?: (id: string) => void;
  sx?: SxStyle;
};

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
} as const;

export default function TableHeadCol({
  headCell,
  colSpan,
  order,
  orderBy,
  sx,
  isLast,
  onSort,
}: Props) {
  let sortableField =
    headCell.sortable &&
    (typeof headCell.sortable === 'string' ? headCell.sortable : headCell.id);
  if (typeof headCell.sortable === 'string') sortableField = headCell.sortable;
  const isSelected = !!sortableField;
  const theme = useTheme();
  const activeOrder = orderBy === sortableField ? order : undefined;

  // Calculate width: size property takes precedence over width/minWidth
  const calculatedWidth = headCell.size
    ? getColumnWidth(headCell.size)
    : headCell.width ?? headCell.minWidth ?? '100%';

  const calculatedMinWidth = headCell.size
    ? getColumnWidth(headCell.size)
    : headCell.minWidth ?? headCell.width;

  // Render label content with optional tooltip
  const labelContent = headCell.tooltip ? (
    <Box sx={SX.tableHeadCellTooltip}>
      {headCell.label}
      <Tooltip title={headCell.tooltip}>
        <Box sx={SX.tableHeadCellTooltipIcon}>
          <Icon name="info-circle-outline" color="grey.400" size={18} />
        </Box>
      </Tooltip>
    </Box>
  ) : (
    headCell.label || ' '
  );

  return (
    <TableCell
      key={headCell.id}
      align={headCell.align || 'left'}
      // sortDirection={isSelected ? order : false}
      colSpan={colSpan}
      sx={{
        ...sx,
        borderStyle: 'hidden',
        width: isLast ? '100%' : calculatedWidth,
        minWidth: calculatedMinWidth,
        maxWidth: calculatedMinWidth,
        background: theme.palette.grey[100],
      }}
      className={headCell.headerClassName}
    >
      {onSort && isSelected ? (
        <TableSortLabel
          sx={SX.tableSortLabel({
            active: orderBy === sortableField,
            sortableField: !!sortableField,
          })}
          active={isSelected}
          direction={isSelected ? activeOrder : 'desc'}
          IconComponent={() => (
            <Box sx={SX.tableSortIconWrapper}>
              <Icon name={getSortIconName(activeOrder)} size={16} />
            </Box>
          )}
          style={{
            cursor: headCell.sortable ? 'pointer' : 'default',
            minWidth: calculatedMinWidth ?? 'max-content',
          }}
          onClick={
            sortableField ? () => onSort(sortableField as string) : undefined
          }
        >
          {labelContent}

          {isSelected ? (
            <Box sx={{ ...visuallyHidden }}>
              {activeOrder === 'desc'
                ? 'sorted descending'
                : activeOrder === 'asc'
                ? 'sorted ascending'
                : 'not sorted'}
            </Box>
          ) : null}
        </TableSortLabel>
      ) : (
        labelContent
      )}
    </TableCell>
  );
}
